import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  CircularProgress,
  Checkbox,
  LinearProgress,
  IconButton,
  Tooltip,
  Paper,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Delete as DeleteIcon, Warning as WarningIcon } from '@mui/icons-material';
import PersonIcon from '@mui/icons-material/Person';
import BuildIcon from '@mui/icons-material/Build';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, startOfYear, endOfYear } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTechnicians } from '../../contexts/TechniciansContext';
import { useSkills } from '../../contexts/SkillsContext';
import { useOnCalls } from '../../contexts/OnCallsContext';
// TODO: Migrer vers les contextes API
// import { techniciansDB, skillsDB, onCallsDB } from '../../db/database';

const BulkDeleteDialog = ({ open, onClose, onDeleted }) => {
  const { technicians } = useTechnicians();
  const { skills } = useSkills();
  const { onCalls, deleteOnCall } = useOnCalls();
  
  const [formData, setFormData] = useState({
    startDate: null,
    endDate: null,
    selectedSkills: [],
    selectedTechnicians: []
  });
  
  const [loading, setLoading] = useState(false);
  const [matchingOnCalls, setMatchingOnCalls] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  useEffect(() => {
    if (formData.startDate || formData.endDate || formData.selectedSkills.length > 0 || formData.selectedTechnicians.length > 0) {
      findMatchingOnCalls();
    } else {
      setMatchingOnCalls([]);
    }
  }, [formData, onCalls]);

  const resetForm = () => {
    setFormData({
      startDate: null,
      endDate: null,
      selectedSkills: [],
      selectedTechnicians: []
    });
    setMatchingOnCalls([]);
    setError('');
  };

  // Pré-sélectionner toutes les compétences après chargement
  useEffect(() => {
    if (skills.length > 0 && formData.selectedSkills.length === 0) {
      setFormData(prev => ({
        ...prev,
        selectedSkills: skills.map(skill => skill.id)
      }));
    }
  }, [skills]);

  // Pré-sélectionner tous les techniciens après chargement
  useEffect(() => {
    if (technicians.length > 0 && formData.selectedTechnicians.length === 0) {
      setFormData(prev => ({
        ...prev,
        selectedTechnicians: technicians.map(tech => tech.id)
      }));
    }
  }, [technicians]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const findMatchingOnCalls = () => {
    let filtered = [...onCalls];

    // Filtrer par période
    if (formData.startDate || formData.endDate) {
      filtered = filtered.filter(onCall => {
        const onCallStart = new Date(onCall.startDate);
        const onCallEnd = new Date(onCall.endDate);
        
        if (formData.startDate && formData.endDate) {
          // L'astreinte doit chevaucher avec la période sélectionnée
          return onCallStart <= formData.endDate && onCallEnd >= formData.startDate;
        } else if (formData.startDate) {
          return onCallEnd >= formData.startDate;
        } else if (formData.endDate) {
          return onCallStart <= formData.endDate;
        }
        return true;
      });
    }

    // Filtrer par techniciens
    if (formData.selectedTechnicians.length > 0) {
      filtered = filtered.filter(onCall => 
        formData.selectedTechnicians.includes(onCall.technicianId)
      );
    }

    // Filtrer par compétences (si l'astreinte a au moins une des compétences sélectionnées)
    if (formData.selectedSkills.length > 0) {
      filtered = filtered.filter(onCall => 
        onCall.skills && onCall.skills.some(skillId => 
          formData.selectedSkills.includes(skillId)
        )
      );
    }

    setMatchingOnCalls(filtered);
  };

  const handleBulkDelete = async () => {
    if (matchingOnCalls.length === 0) {
      setError('Aucune astreinte à supprimer');
      return;
    }

    setLoading(true);
    try {
      // Supprimer toutes les astreintes correspondantes
      for (const onCall of matchingOnCalls) {
        await deleteOnCall(onCall.id);
      }

      onDeleted(matchingOnCalls.length);
      handleClose();
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      setError('Erreur lors de la suppression: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const renderOnCallItem = (onCall) => {
    const technician = technicians.find(t => t.id === onCall.technicianId);
    const onCallSkills = (onCall.skills || []).map(skillId => 
      skills.find(s => s.id === skillId)
    ).filter(Boolean);

    return (
      <ListItem key={onCall.id}>
        <ListItemIcon>
          <PersonIcon />
        </ListItemIcon>
        <ListItemText
          primary={technician ? `${technician.firstName} ${technician.lastName}` : 'Technicien inconnu'}
          secondary={
            <Box>
              <Typography variant="body2" color="text.secondary">
                {format(new Date(onCall.startDate), 'dd/MM/yyyy HH:mm', { locale: fr })} - {format(new Date(onCall.endDate), 'dd/MM/yyyy HH:mm', { locale: fr })}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                {onCallSkills.map(skill => (
                  <Chip
                    key={skill.id}
                    label={skill.name}
                    size="small"
                    sx={{ 
                      backgroundColor: skill.color + '20',
                      fontSize: '0.7rem',
                      height: '20px'
                    }}
                  />
                ))}
              </Box>
            </Box>
          }
        />
      </ListItem>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DeleteIcon color="error" />
        Suppression en masse des astreintes
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <DatePicker
              label="Date de début (optionnel)"
              value={formData.startDate}
              onChange={(date) => handleFormChange('startDate', date)}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DatePicker
              label="Date de fin (optionnel)"
              value={formData.endDate}
              onChange={(date) => handleFormChange('endDate', date)}
              renderInput={(params) => <TextField {...params} fullWidth />}
              minDate={formData.startDate}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Compétences (filtre)</InputLabel>
              <Select
                multiple
                value={formData.selectedSkills}
                onChange={(e) => handleFormChange('selectedSkills', e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((skillId) => {
                      const skill = skills.find(s => s.id === skillId);
                      return (
                        <Chip
                          key={skillId}
                          label={skill?.name || skillId}
                          size="small"
                          sx={{ backgroundColor: skill?.color + '20' }}
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {skills.map((skill) => (
                  <MenuItem key={skill.id} value={skill.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          backgroundColor: skill.color,
                          borderRadius: '50%'
                        }}
                      />
                      {skill.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Techniciens (filtre)</InputLabel>
              <Select
                multiple
                value={formData.selectedTechnicians}
                onChange={(e) => handleFormChange('selectedTechnicians', e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((techId) => {
                      const tech = technicians.find(t => t.id === techId);
                      return (
                        <Chip
                          key={techId}
                          label={tech ? `${tech.firstName} ${tech.lastName}` : techId}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {technicians.map((tech) => (
                  <MenuItem key={tech.id} value={tech.id}>
                    <PersonIcon sx={{ mr: 1 }} />
                    {tech.firstName} {tech.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Astreintes correspondantes ({matchingOnCalls.length})
        </Typography>

        {matchingOnCalls.length > 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2">
              Attention : {matchingOnCalls.length} astreinte(s) sera(ont) supprimée(s) définitivement
            </Typography>
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            Aucune astreinte ne correspond aux critères sélectionnés
          </Alert>
        )}

        <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
          <List dense>
            {matchingOnCalls.map(renderOnCallItem)}
          </List>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Annuler</Button>
        <Button 
          onClick={handleBulkDelete}
          variant="contained"
          color="error"
          disabled={loading || matchingOnCalls.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
        >
          {loading ? 'Suppression...' : `Supprimer ${matchingOnCalls.length} astreinte(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkDeleteDialog; 